import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AssistanceRequestNotice from "./AssistanceRequestNotice";
import "./HowSystemWorksModal.css";

const VIDEO_ID = "f74583795d1a1c17d89084b06d7a68f5";
const NOTICE_SEEN_KEY = "tarahum:assistance-request-notice:seen:v1";
export const HOW_SYSTEM_WORKS_EVENT = "app:open-how-system-works";

export function openHowSystemWorks() {
  window.dispatchEvent(new CustomEvent(HOW_SYSTEM_WORKS_EVENT));
}

export function GlobalHowSystemWorks() {
  const [pathname, setPathname] = useState(() => window.location.pathname.toLowerCase());

  useEffect(() => {
    const updatePathname = () => setPathname(window.location.pathname.toLowerCase());
    window.addEventListener("popstate", updatePathname);
    window.addEventListener("app:navigate", updatePathname);
    return () => {
      window.removeEventListener("popstate", updatePathname);
      window.removeEventListener("app:navigate", updatePathname);
    };
  }, []);

  const isArabic = pathname === "/ar" || pathname.startsWith("/ar/");
  const routePath = pathname.startsWith("/ar/") ? pathname.slice(3) || "/" : pathname;
  const isHome = routePath === "/" || routePath === "/home" || routePath === "/home.html" || pathname === "/ar" || pathname === "/ar/" || pathname === "/ar/index.html";
  const language = isArabic ? "ar" : "en";

  return (
    <>
      <HowSystemWorksMenuItem key={pathname} language={language} />
      <HowSystemWorksModal language={language} autoOpen={isHome} />
    </>
  );
}

export function HowSystemWorksMenuItem({ language = "en" }) {
  const [menuTarget, setMenuTarget] = useState(null);
  const isArabic = language === "ar";

  useEffect(() => {
    const findMenu = () => {
      const selectors = [
        "header .navbar-nav",
        "header nav > ul",
        "header nav ul",
        ".main-menu > ul",
        ".navigation > ul",
        "nav .navbar-nav",
      ];
      const target = selectors
        .flatMap((selector) => Array.from(document.querySelectorAll(selector)))
        .find((element) => !element.closest("footer") && element.querySelector("a, button"));
      if (target) setMenuTarget(target);
      return Boolean(target);
    };

    if (findMenu()) return undefined;
    const observer = new MutationObserver(() => {
      if (findMenu()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const button = (
    <button
      type="button"
      className="system-video-menu-link"
      onClick={openHowSystemWorks}
      aria-haspopup="dialog"
    >
      <span className="system-video-menu-link__icon" aria-hidden="true">▶</span>
      <span>{isArabic ? "كيف يعمل النظام" : "How System Works"}</span>
    </button>
  );

  if (!menuTarget) {
    return <div className="system-video-menu-fallback">{button}</div>;
  }

  return createPortal(<li className="system-video-menu-item">{button}</li>, menuTarget);
}

export default function HowSystemWorksModal({ language = "en", autoOpen = false }) {
  const [openPopup, setOpenPopup] = useState(null);
  const closeButtonRef = useRef(null);
  const isArabic = language === "ar";

  const close = useCallback(() => setOpenPopup(null), []);

  useEffect(() => {
    let timer;
    const openVideo = () => {
      if (timer) window.clearTimeout(timer);
      setOpenPopup("video");
    };
    window.addEventListener(HOW_SYSTEM_WORKS_EVENT, openVideo);

    try {
      if (autoOpen && !window.localStorage.getItem(NOTICE_SEEN_KEY)) {
        timer = window.setTimeout(() => {
          setOpenPopup("notice");
          window.localStorage.setItem(NOTICE_SEEN_KEY, "true");
        }, 650);
      }
    } catch {
      // Storage may be unavailable in private browsing; manual video opening still works.
    }

    return () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener(HOW_SYSTEM_WORKS_EVENT, openVideo);
    };
  }, [autoOpen]);

  useEffect(() => {
    if (!openPopup) return undefined;

    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [close, openPopup]);

  if (!openPopup) return null;

  const isNotice = openPopup === "notice";

  const copy = isArabic
    ? {
        eyebrow: "دليل سريع",
        title: "كيف يعمل النظام؟",
        description: "شاهد هذا الفيديو القصير للتعرّف على أهم خطوات استخدام النظام بسهولة.",
        close: "إغلاق الفيديو",
      }
    : {
        eyebrow: "Quick guide",
        title: "How does the system work?",
        description: "Watch this short guide to learn the main steps and start using the system with confidence.",
        close: "Close video",
      };

  return (
    <div
      className={`system-video-modal${isNotice ? " system-video-modal--notice" : ""}`}
      role="presentation"
      dir={isArabic ? "rtl" : "ltr"}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <section
        className={`system-video-modal__dialog${isNotice ? " system-video-modal__dialog--notice" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={isNotice ? "home-assistance-notice-title" : "system-video-title"}
      >
        <div className="system-video-modal__glow" aria-hidden="true" />
        <button
          ref={closeButtonRef}
          type="button"
          className="system-video-modal__close"
          onClick={close}
          aria-label={copy.close}
        >
          <span aria-hidden="true">×</span>
        </button>

        {isNotice ? (
          <div className="system-video-modal__notice-content">
            <AssistanceRequestNotice language={language} />
          </div>
        ) : (
          <>
            <header className="system-video-modal__header">
              <span className="system-video-modal__eyebrow">
                <span className="system-video-modal__play" aria-hidden="true">▶</span>
                {copy.eyebrow}
              </span>
              <h2 id="system-video-title">{copy.title}</h2>
              <p>{copy.description}</p>
            </header>

            <div className="system-video-modal__player">
              <iframe
                src={`https://iframe.videodelivery.net/${VIDEO_ID}?preload=metadata`}
                title={copy.title}
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </>
        )}
      </section>
    </div>
  );
}
